import {
   DocumentSendError,
   MedicalLeaveApproveCommand,
   PueConnectionFault,
   PueFault,
   ValidPassportIdentityDocumentRequiredFault,
   ZlaDocumentsSendFault,
} from '@dreryk/egabinet-ezla-frontend-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { CertificateFault, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';

import { commonTranslationKeys, medicalLeaveFormTranslationKeys } from '../../../../../translations';
import {
   mapMedicalLeaveFormAddressToPatientAddress,
   mapMedicalLeavePuePayersToMedicalLeavePayerApprove,
} from '../../../common/common.utils';
import { findEndDate, findStartDate } from '../../../common/formSections/leaveDateRange/leaveDateRange.utils';
import { MedicalLeaveFormBean } from '../../medicalLeave.types';

type MedicalLeaveApproveCommandParameters = {
   formData: MedicalLeaveFormBean;
   externalId: string;
};

type UseMedicalLeaveApproveCommandOptions = {
   onSuccess: VoidFunction;
   onPueFault: (fault: PueFault) => void;
   onZlaDocumentSendFault: (zlaDocumentSendErrors: DocumentSendError[]) => void;
   onAuthenticationRequiredFault: VoidFunction;
   onValidPassportIdentityDocumentRequiredFault: VoidFunction;
};

const useMedicalLeaveApproveCommand = (
   options: UseMedicalLeaveApproveCommandOptions
): UseCqrsCommandResult<
   void,
   MedicalLeaveApproveCommandParameters,
   CertificateFault | PueFault | PueConnectionFault | ValidPassportIdentityDocumentRequiredFault | ZlaDocumentsSendFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new MedicalLeaveApproveCommand();
         command.externalId = parameters.externalId;
         const {
            insuranceType,
            payers,
            leaveAfterTerminationOfEmployment,
            leaveDateRange,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            leaveDaysCount,
            diagnosis,
            address,
            reason,
            ...restFormValues
         } = parameters.formData;
         command.medicalLeaveDetails = {
            address: mapMedicalLeaveFormAddressToPatientAddress(address!),
            icd10Code: diagnosis!.getCode().asString(),
            dateFrom: findStartDate(leaveDateRange)!,
            dateTo: findEndDate(leaveDateRange)!,
            insuranceType: insuranceType,
            payers: mapMedicalLeavePuePayersToMedicalLeavePayerApprove(payers, leaveAfterTerminationOfEmployment),
            reason: Choice.withUseDefaultIfNull(reason),
            ...restFormValues,
         };
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(commonTranslationKeys.submitSuccess);
            options.onSuccess();
         },
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: options.onAuthenticationRequiredFault,
            }),
            'egabinet-ezla-frontend-api:PueConnectionFault': () => {
               dispatchSnackbarNegativeEvent(medicalLeaveFormTranslationKeys.pueNotAvailable);
            },
            'egabinet-ezla-frontend-api:PueFault': (fault) => {
               dispatchSnackbarNegativeEvent(commonTranslationKeys.submitError);
               options.onPueFault(fault);
            },
            'egabinet-ezla-frontend-api:ValidPassportIdentityDocumentRequiredFault': () => {
               options.onValidPassportIdentityDocumentRequiredFault();
            },
            'egabinet-ezla-frontend-api:ZlaDocumentsSendFault': (fault) => {
               options.onZlaDocumentSendFault(fault.errors!);
            },
         },
      }
   );
};

export { useMedicalLeaveApproveCommand };
